/**
 * TanStack Query client + localStorage persister + cross-tab broadcast (iter 257).
 *
 * Why this exists:
 *   The app historically rolled its own stale-while-revalidate pattern with
 *   `localStorage.setItem('swr:...')` scattered across 6+ files. TanStack
 *   Query gives us that same UX (show cached data instantly, fetch fresh
 *   in background) + proper invalidation, deduping, retries, and devtools.
 *
 * Deployed defaults:
 *   - staleTime = 30s — keeps re-renders cheap while data is fresh.
 *   - gcTime = 10min — data persists in the cache long enough to
 *     bridge navigations without refetching.
 *   - retry = 2 — matches existing axiosRetry behavior.
 *   - refetchOnWindowFocus = true — matches the "come back to tab" UX
 *     users expect from the existing SWR pattern.
 *
 * Persister:
 *   localStorage keeps cache across page reloads so returning users see
 *   instant data on dashboards (same UX the old `swr:*` localStorage
 *   pattern provided).
 *
 * Cross-tab broadcast (iter 257):
 *   A dispatcher typically has 3+ tabs open (kanban, service calls,
 *   route builder). `broadcastQueryClient` propagates every
 *   `setQueryData` / `invalidateQueries` across tabs instantly via the
 *   BroadcastChannel API — so a status flip on one tab is reflected
 *   on all others in real time, without waiting for the next WebSocket
 *   event or polling tick.
 */
import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { broadcastQueryClient } from "@tanstack/query-broadcast-client-experimental";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Cross-tab sync. Any setQueryData/invalidateQueries in one tab fires the
// same effect on sibling tabs. Falls back gracefully if the browser
// doesn't support BroadcastChannel (Safari <15.4 etc.).
if (typeof window !== "undefined" && typeof window.BroadcastChannel === "function") {
  try {
    broadcastQueryClient({
      queryClient,
      broadcastChannel: "jcc-query-cache-v1",
    });
    // Expose for smoke tests & devtools (non-prod only — harmless in prod).
    window.__queryClient = queryClient;
  } catch (e) {
    // Non-fatal — cross-tab sync is a nice-to-have, not required.
    console.warn("broadcastQueryClient setup failed:", e);
  }
}

export const queryPersister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "tq:v1",
  throttleTime: 1000,
});

// Persisted cache key prefix — if we ever need to hard-invalidate after
// a breaking schema change, bump this version.
export const PERSIST_BUSTER = "v1";
